import { Component, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { I18nService, SidebarStateService, TenantService } from '../../core';
import { TPipe } from '../../shared/pipes';
import { SidebarNavItemComponent } from './components/sidebar-nav-item/sidebar-nav-item.component';
import { SidebarProjectComponent, type SidebarProjectVm } from './components/sidebar-project/sidebar-project.component';
import { SidebarSectionTitleComponent } from './components/sidebar-section-title/sidebar-section-title.component';
import { CreateProjectModalComponent } from '../../features/projects/components/create-project-modal/create-project-modal.component';
import { CreateWorkflowFromProjectModalComponent } from '../../features/workflows/components/create-workflow-from-project-modal/create-workflow-from-project-modal.component';
import { CompanySwitcherComponent } from '../../features/company-context/components/company-switcher/company-switcher.component';
import { OnboardingChecklistComponent } from '../../features/onboarding/components/onboarding-checklist/onboarding-checklist.component';
import { SidebarStore } from './store/sidebar.store';
import { InboxStore } from '../../features/inbox/store/inbox.store';
import { AssignedCommentsStore } from '../../features/assigned-comments/store/assigned-comments.store';
import { WorkflowsFacade } from '../../features/workflows/facades/workflows.facade';
import { ProjectsFacade } from '../../features/projects/facades/projects.facade';

interface SidebarNavItemConfig {
  labelKey: string;
  icon: string;
  routeSegment: string;
  exact?: boolean;
}

interface SidebarNavItemVm extends SidebarNavItemConfig {
  route: string;
  label: string;
  isActive: boolean;
  badgeCount?: number;
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    RouterLink,
    LucideAngularModule,
    TPipe,
    CompanySwitcherComponent,
    OnboardingChecklistComponent,
    SidebarNavItemComponent,
    SidebarProjectComponent,
    SidebarSectionTitleComponent,
    CreateProjectModalComponent,
    CreateWorkflowFromProjectModalComponent,
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.css',
})
export class SidebarComponent {
  private readonly router = inject(Router);
  private readonly sidebarState = inject(SidebarStateService);
  private readonly sidebarStore = inject(SidebarStore);
  private readonly i18n = inject(I18nService);
  private readonly inboxStore = inject(InboxStore);
  private readonly assignedCommentsStore = inject(AssignedCommentsStore);
  private readonly workflowsFacade = inject(WorkflowsFacade);
  private readonly projectsFacade = inject(ProjectsFacade);
  private readonly tenant = inject(TenantService);

  private readonly currentUrl = signal<string>(this.router.url);
  readonly isCollapsed = this.sidebarState.isCollapsed;
  readonly expandedProjectIds = signal<Set<string>>(new Set());
  readonly isCreateProjectModalOpen = signal(false);
  readonly isCreateWorkflowModalOpen = signal(false);
  readonly createWorkflowProjectId = signal<string | null>(null);
  readonly createWorkflowProjectName = signal('');
  readonly sidebarLoading = this.sidebarStore.loading;
  readonly sidebarError = this.sidebarStore.error;
  readonly isSidebarEmpty = this.sidebarStore.isEmpty;
  readonly t = this.i18n.t.bind(this.i18n);

  private readonly primaryNavConfig: SidebarNavItemConfig[] = [
    { labelKey: 'nav.home', icon: 'layout-grid', routeSegment: 'dashboard', exact: true },
    { labelKey: 'nav.inbox', icon: 'inbox', routeSegment: 'inbox', exact: false },
    {
      labelKey: 'nav.assignedComments',
      icon: 'message-square',
      routeSegment: 'assigned-comments',
      exact: false,
    },
    { labelKey: 'nav.myTasks', icon: 'check-square', routeSegment: 'my-tasks', exact: false },
    { labelKey: 'nav.projects', icon: 'folder', routeSegment: 'projects', exact: false },
    { labelKey: 'nav.workflows', icon: 'refresh-cw', routeSegment: 'workflows', exact: false },
  ];

  readonly primaryNavItems = computed<SidebarNavItemVm[]>(() => {
    void this.i18n.locale();
    const url = this.currentUrl();
    const base = this.getCompanyBaseRoute(url);
    const unreadInboxCount = this.inboxStore.unreadCount();
    const unreadAssignedCommentsCount = this.assignedCommentsStore.unreadCount();
    return this.primaryNavConfig.map((item) => ({
      ...item,
      route: `${base}/${item.routeSegment}`,
      label: this.t(item.labelKey),
      isActive: this.isPrimaryNavItemActive(item, url, base),
      badgeCount:
        item.routeSegment === 'inbox'
          ? unreadInboxCount
          : item.routeSegment === 'assigned-comments'
            ? unreadAssignedCommentsCount
            : 0,
    }));
  });

  readonly projectTree = computed<SidebarProjectVm[]>(() => {
    void this.i18n.locale();
    const url = this.currentUrl();
    const base = this.getCompanyBaseRoute(url);
    const routeContext = this.parseRouteContext(url);
    return this.sidebarStore.projects().map((project) => ({
      id: project.id,
      name: project.name,
      initials: project.initials,
      coverImageUrl: project.coverImageUrl ?? null,
      iconKey: project.iconKey ?? null,
      colorHex: project.colorHex ?? null,
      route: `${base}/projects/${project.id}`,
      isActive: routeContext.projectId === project.id,
      workflows: project.workflows.map((workflow) => ({
        id: workflow.id,
        name: workflow.name?.trim() || workflow.id || this.t('domain.workflow'),
        icon: workflow.icon,
        colorHex: workflow.colorHex,
        route: `${base}/projects/${project.id}/workflows/${workflow.id}`,
        isActive: routeContext.workflowId === workflow.id,
      })),
    }));
  });

  constructor() {
    const context = this.parseRouteContext(this.router.url);
    if (context.projectId) {
      this.expandedProjectIds.set(new Set([context.projectId]));
    }

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe((event) => {
        this.currentUrl.set(event.urlAfterRedirects);
        const routeState = this.parseRouteContext(event.urlAfterRedirects);
        if (routeState.projectId) {
          this.expandedProjectIds.set(new Set([routeState.projectId]));
        }
      });
  }

  isProjectExpanded(projectId: string): boolean {
    return this.expandedProjectIds().has(projectId);
  }

  toggleProject(projectId: string): void {
    this.expandedProjectIds.update((prev) => {
      if (prev.has(projectId)) return new Set<string>();
      return new Set([projectId]);
    });
  }

  openCreateWorkflowModal(project: SidebarProjectVm): void {
    this.createWorkflowProjectId.set(project.id);
    this.createWorkflowProjectName.set(project.name);
    this.isCreateWorkflowModalOpen.set(true);
  }

  closeCreateWorkflowModal(): void {
    this.isCreateWorkflowModalOpen.set(false);
    this.createWorkflowProjectId.set(null);
    this.createWorkflowProjectName.set('');
  }

  async onWorkflowCreated(workflowId: string): Promise<void> {
    const projectId = this.createWorkflowProjectId();
    await this.workflowsFacade.reloadWorkflowsAsync();
    void this.sidebarStore.loadSidebar({ force: true });
    if (projectId) {
      this.expandedProjectIds.update((prev) => new Set([...prev, projectId]));
    }
    this.closeCreateWorkflowModal();
    if (workflowId) {
      const companyKey = this.getCurrentCompanyKey(this.router.url);
      if (companyKey) {
        const target = projectId
          ? ['/c', companyKey, 'projects', projectId, 'workflows', workflowId]
          : ['/c', companyKey, 'workflows', workflowId];
        void this.router.navigate(target);
      }
    }
  }

  openCreateProjectModal(): void {
    this.isCreateProjectModalOpen.set(true);
  }

  closeCreateProjectModal(): void {
    this.isCreateProjectModalOpen.set(false);
  }

  async onProjectCreated(projectId: string): Promise<void> {
    this.isCreateProjectModalOpen.set(false);
    this.projectsFacade.retry();
    await this.workflowsFacade.reloadWorkflowsAsync();
    await this.sidebarStore.loadSidebar({ force: true });
    const companyKey = this.getCurrentCompanyKey(this.router.url);
    if (!companyKey) return;
    if (projectId) {
      await this.router.navigate(['/c', companyKey, 'projects', projectId]);
      return;
    }
    await this.router.navigate(['/c', companyKey, 'projects']);
  }

  retrySidebar(): void {
    void this.sidebarStore.loadSidebar({ force: true });
  }

  private parseRouteContext(url: string): { projectId: string | null; workflowId: string | null } {
    const projectMatch = url.match(/\/c\/[^\/?#]+\/projects\/([^\/?#]+)(?:\/workflows\/([^\/?#]+))?/);
    if (projectMatch) {
      return {
        projectId: projectMatch[1] ?? null,
        workflowId: projectMatch[2] ?? null,
      };
    }

    const workflowMatch = url.match(/\/c\/[^\/?#]+\/workflows\/([^\/?#]+)/);
    if (workflowMatch) {
      const workflowId = workflowMatch[1] ?? null;
      const workflow = this.sidebarStore
        .projects()
        .flatMap((project) => project.workflows)
        .find((item) => item.id === workflowId);
      return {
        projectId: workflow?.projectId ?? null,
        workflowId,
      };
    }

    return { projectId: null, workflowId: null };
  }

  private isRouteActive(currentUrl: string, route: string, exact: boolean): boolean {
    if (exact) {
      return currentUrl === route;
    }
    return currentUrl.startsWith(route);
  }

  private isPrimaryNavItemActive(item: SidebarNavItemConfig, url: string, base: string): boolean {
    if (item.routeSegment === 'projects') {
      // Em paginas de detalhe (projeto/workflow), o contexto ativo fica na arvore.
      return url === `${base}/projects` || url.startsWith(`${base}/projects?`);
    }
    if (item.routeSegment === 'workflows') {
      // Destaca apenas na listagem global de workflows.
      return url === `${base}/workflows` || url.startsWith(`${base}/workflows?`);
    }
    return this.isRouteActive(url, `${base}/${item.routeSegment}`, !!item.exact);
  }

  private getCurrentCompanyKey(url: string): string | null {
    const match = url.match(/\/c\/([^\/?#]+)/);
    return match?.[1] ?? null;
  }

  private getCompanyBaseRoute(url: string): string {
    const fromUrl = this.getCurrentCompanyKey(url);
    if (fromUrl) return `/c/${fromUrl}`;
    const key = this.tenant.companyKey();
    return key ? `/c/${key}` : '/auth/login';
  }

}
